import React, {
  useMemo, useEffect, useCallback
} from 'react';
import { oneOf } from 'prop-types';
import moment from 'moment';
import { debounce } from 'lodash-es';
import { useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { hasPermission, manageSurveyPermission } from 'unihow-common';
import Pagination from '../../Pagination';
import { getSurveys } from '../../../api';
import { getTranslation, langType, withLang } from '../../../i18n';
import sharedStyles from '../../../styles/shared';
import { EMPTY_PAGE, RESULTS_PER_PAGE } from '../../../constants';
import { surveyTypeValues } from '../../../constants/types';
import ListingRow from './ListingRow';
import SurveySearch from './SurveySearch';
import { validateApiDateStr } from '../../../util';
import SelectSchemaDialog from '../../SelectSchemaDialog';

import styles from './surveyList.module.css';
import useUrlSearch from '../../../hooks/useUrlSearch';
import useAsyncQuery from '../../../hooks/useAsyncQuery';

const { center, right } = sharedStyles;

const COMMA_DELIMITED_PARAMETERS = ['degreeProgramCodes'];
const LISTING_TYPE = 'FOR_VIEWER';

const deduceSurveyState = ({ startDate, started, ended }) => {
  if (startDate && !started && !ended) {
    return 'common.pending';
  }
  if (ended && (!started || moment(ended) >= moment(started))) {
    return 'common.closed';
  }
  if (started && (!ended || moment(ended) < moment(started))) {
    return 'common.open';
  }
  return '';
};

const parseFromSearchParams = (sp) => {
  const page = Number(sp.get('page') ?? 0) || 0;
  const title = sp.get('title') || undefined;
  const degreeProgramCodes = sp.get('degreeProgramCodes') || undefined;
  const states = sp.getAll('states') || undefined;
  const startDateAfter = sp.get('startDateAfter') || undefined;
  const startDateBefore = sp.get('startDateBefore') || undefined;

  return {
    page,
    optionalSearchParams: {
      title,
      degreeProgramCodes,
      states: states.length ? states : undefined,
      startDateAfter: validateApiDateStr(startDateAfter) ? startDateAfter : undefined,
      startDateBefore: validateApiDateStr(startDateBefore) ? startDateBefore : undefined
    }
  };
};

const SurveyList = ({
  type, title, translate: t, lang
}) => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const canStartSurvey = hasPermission(currentUser.permissions, manageSurveyPermission(type));

  const {
    searchParams, page, setPage, setParams
  } = useUrlSearch({ commaKeys: COMMA_DELIMITED_PARAMETERS });

  const { optionalSearchParams } = useMemo(
    () => parseFromSearchParams(searchParams),
    [searchParams]
  );

  const fetchPage = useCallback(() => getSurveys(
    optionalSearchParams.states,
    page,
    RESULTS_PER_PAGE,
    type,
    LISTING_TYPE,
    optionalSearchParams
  ), [optionalSearchParams, page, type]);

  const surveys = useAsyncQuery(fetchPage, { initialData: EMPTY_PAGE });

  const debouncedCriteriaChange = useMemo(() =>
    debounce(setParams, 800), [setParams]);

  useEffect(() =>
    () => debouncedCriteriaChange.cancel(), // clean-up on unmount
  [debouncedCriteriaChange]);

  const defaultSearchCriteria = useMemo(() => {
    const criteria = {};

    Object.entries(optionalSearchParams).forEach(([key, value]) => {
      if (COMMA_DELIMITED_PARAMETERS.includes(key)) {
        criteria[key] = value ? value.split(',') : [];
      } else criteria[key] = value;
    });

    return criteria;
  }, [optionalSearchParams]);

  return (
    <div className="listing">
      <h2 className="panel__title">{title}</h2>
      {canStartSurvey && (
      <SelectSchemaDialog schemaType={type}>
        <button
          type="button"
          className="button--action icon--plus"
          data-cy="start-survey-button"
        >
          {t('manageSurveys.startSurvey')}
        </button>
      </SelectSchemaDialog>
      )}

      <h3 className={styles.browseSurveys}>{t('manageSurveys.browseSurveys')}</h3>

      <SurveySearch
        key={type}
        onChange={debouncedCriteriaChange}
        defaultCriteria={defaultSearchCriteria}
        type={type}
      />

      <h3>{`${surveys.totalElements} ${t('manageSurveys.nSurveys')}`}</h3>

      <table className="table-simple" data-cy="manage-surveys-table">
        <thead>
          <tr>
            <th>{t('common.name')}</th>
            <th className={right}><Translate id="manageSurveys.state" /></th>
            <th className={center}><Translate id="manageSurveys.responseTime" /></th>
            <th className={center}><Translate id="manageSurveys.numberOfResponses" /></th>
            <th className={center}><Translate id="manageSurveys.quickSteps" /></th>
          </tr>
        </thead>
        <tbody>
          {surveys.content.map((survey) => (
            <ListingRow
              key={survey.id}
              uid={survey.id.toString()}
              title={getTranslation(survey.title, lang)}
              type={type}
              code={survey.code}
              surveyState={deduceSurveyState(survey.surveySchedule)}
              startDate={survey.surveySchedule.started || survey.surveySchedule.startDate}
              endDate={survey.surveySchedule.ended || survey.surveySchedule.endDate}
              translate={t}
              hasEnoughResponses={survey.hasEnoughResponses}
              participantCount={survey.participantCount}
              responseCount={survey.responseCount}
            />
          ))}
        </tbody>
      </table>

      <Pagination
        navFunction={setPage}
        totalPages={surveys.totalPages}
        first={surveys.first}
        last={surveys.last}
        number={surveys.number}
        translate={t}
      />
    </div>
  );
};

SurveyList.propTypes = {
  type: oneOf(Object.values(surveyTypeValues)).isRequired,
  ...langType
};

export default withLang(SurveyList);
